import React, { useState, useRef, useEffect } from 'react';
import { ThreadMessage } from '../../types';
import { useThreadMessages } from '../../hooks/useThreadMessages';
import { createThreadMessage, batchMarkThreadMessagesRead, markThreadReadForRole } from '../../services/firestoreService';
import { useKeyboardVisible } from '../../hooks/useKeyboardVisible';
import './ConversationThread.css';

export interface ConversationThreadProps {
  submissionId: string;
  organizationId: string;
  projectId: string;
  participantId: string;
  currentUserUid: string;
  currentUserName: string;
  currentUserRole: 'researcher' | 'participant';
  /** Pre-loaded messages (participant feed passes these down). If omitted, the component
   *  activates its own per-submission Firestore listener (used by researcher SubmissionModal). */
  messages?: ThreadMessage[];
  /** Task name — passed through to thread summary doc for inbox display */
  taskName?: string;
}

function formatCommentTime(createdAt: any): string {
  if (!createdAt) return '';
  const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  if (isNaN(date.getTime())) return '';
  const ms = Date.now() - date.getTime();
  if (ms < 60000) return 'now';
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h`;
  const days = Math.floor(ms / 86400000);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** First letter of first name, uppercased */
function initial(name: string): string {
  return (name || '?')[0].toUpperCase();
}

export const ConversationThread: React.FC<ConversationThreadProps> = ({
  submissionId,
  organizationId,
  projectId,
  participantId,
  currentUserUid,
  currentUserName,
  currentUserRole,
  messages: externalMessages,
  taskName,
}) => {
  // Use external messages if provided; otherwise activate own listener
  const hook = useThreadMessages(externalMessages ? null : submissionId, currentUserRole, organizationId);
  const messages = externalMessages ?? hook.messages;

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sentConfirm, setSentConfirm] = useState(false);
  const [optimisticMsgs, setOptimisticMsgs] = useState<ThreadMessage[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const keyboardUp = useKeyboardVisible();

  // Clear optimistic messages when real messages arrive containing them
  const allMessages = [...messages, ...optimisticMsgs.filter(
    om => !messages.some(m => m.body === om.body && m.senderUid === om.senderUid)
  )];

  // Mark unread messages from other party as read when this thread renders
  useEffect(() => {
    if (!messages.length) return;
    const oppositeRole = currentUserRole === 'researcher' ? 'participant' : 'researcher';
    const unreadIds = messages
      .filter(m => m.senderRole === oppositeRole && !m.readByRecipient)
      .map(m => m.id);
    if (unreadIds.length > 0) {
      batchMarkThreadMessagesRead(unreadIds).catch(() => {});
      // Also reset the thread-level unread counter
      markThreadReadForRole(submissionId, currentUserRole).catch(() => {});
    }
  }, [messages, currentUserRole, submissionId]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || sending) return;

    // Optimistic: show message immediately
    const optimistic: ThreadMessage = {
      id: `optimistic-${Date.now()}`,
      organizationId,
      projectId,
      submissionId,
      participantId,
      senderUid: currentUserUid,
      senderRole: currentUserRole,
      senderName: currentUserName,
      body,
      createdAt: new Date().toISOString(),
      readByRecipient: false,
      messageType: 'comment',
    };
    setOptimisticMsgs(prev => [...prev, optimistic]);
    setDraft('');

    setSending(true);
    try {
      await createThreadMessage({
        organizationId,
        projectId,
        submissionId,
        participantId,
        senderUid: currentUserUid,
        senderRole: currentUserRole,
        senderName: currentUserName,
        body,
        createdAt: null,
        readByRecipient: false,
        messageType: 'comment',
      }, taskName);
      // Show brief "Sent" confirmation
      setSentConfirm(true);
      setTimeout(() => setSentConfirm(false), 2000);
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove optimistic message on failure
      setOptimisticMsgs(prev => prev.filter(m => m.id !== optimistic.id));
      setDraft(body); // restore draft
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (allMessages.length === 0 && !externalMessages) {
    // Researcher-side empty state — show prompt
    return (
      <div className="ct-zone">
        {sentConfirm && (
          <div style={{ textAlign: 'right', fontSize: 11, color: '#6E7A5A', fontWeight: 600, padding: '2px 8px' }}>
            Sent
          </div>
        )}
        <div className="ct-input-row">
          <input
            ref={inputRef}
            className="ct-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Leave feedback..."
            disabled={sending}
          />
          <button className="ct-send" onClick={handleSend} disabled={!draft.trim() || sending}>
            <SendIcon />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`ct-zone${keyboardUp ? ' ct-keyboard-up' : ''}`}>
      {/* Comments list */}
      <div className="ct-comments">
        {allMessages.map((msg) => (
          <Comment
            key={msg.id}
            message={msg}
            isParticipant={msg.senderRole === 'participant'}
          />
        ))}
        {sentConfirm && (
          <div style={{ textAlign: 'right', fontSize: 11, color: '#6E7A5A', fontWeight: 600, padding: '2px 8px' }}>
            Sent
          </div>
        )}
      </div>

      {/* Reply input */}
      <div className="ct-input-row">
        <input
          ref={inputRef}
          className="ct-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={currentUserRole === 'researcher' ? 'Leave feedback...' : 'Reply...'}
          disabled={sending}
        />
        <button className="ct-send" onClick={handleSend} disabled={!draft.trim() || sending}>
          <SendIcon />
        </button>
      </div>
    </div>
  );
};

/* ── Single comment ── */
const Comment: React.FC<{ message: ThreadMessage; isParticipant: boolean }> = ({ message, isParticipant }) => {
  const time = formatCommentTime(message.createdAt);
  const msgType = message.messageType || 'comment';

  // System messages — centered, no avatar
  if (msgType === 'system') {
    return (
      <div className="ct-system">
        <span className="ct-system-text">{message.body}</span>
        {time && <span className="ct-system-time">{time}</span>}
      </div>
    );
  }

  // Redo request — highlighted card treatment
  if (msgType === 'redo_request') {
    return (
      <div className="ct-redo-request">
        <div className="ct-redo-header">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1,4 1,10 7,10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
          <span>Redo requested by {message.senderName}</span>
        </div>
        <p className="ct-redo-body">{message.body}</p>
        {time && <span className="ct-comment-time">{time}</span>}
      </div>
    );
  }

  return (
    <div className={`ct-comment ${isParticipant ? 'ct-comment-reply' : ''}`}>
      {/* Avatar — dark circle for researcher, light for participant */}
      <div className={`ct-avatar ${isParticipant ? 'ct-avatar-participant' : 'ct-avatar-researcher'}`}>
        <span className="ct-avatar-letter">{initial(message.senderName)}</span>
      </div>

      <div className="ct-comment-body">
        <span className="ct-comment-name">{message.senderName}</span>
        <span className="ct-comment-text">{message.body}</span>
        <span className="ct-comment-time">
          {time}
          {message.readByRecipient && <span className="ct-comment-read"> · Read</span>}
        </span>
      </div>
    </div>
  );
};

/* ── Send icon (ember CTA) ── */
const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22,2 15,22 11,13 2,9" />
  </svg>
);
